/**
 * CockpitSessionsView — full-page conversations with inline chat.
 *
 * Split layout: session list on the left, full chat thread on the right.
 * Clicking a session loads its messages and lets the user continue chatting
 * directly on this page (no floating panel).
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/useAuth';
import { useCockpitContext } from '../hooks/useCockpitContext';
import { useThemeClasses } from '../providers/ThemeProvider';
import { cx } from '../styles';
import { useCockpitStore } from '../store/CockpitStore';
import { MessageBubble, StreamingMessage } from '../components/cockpit/MessageBubble';
import { ChatComposer } from '../components/cockpit/ChatComposer';
import type { CockpitSession } from '../services/CockpitService';

const CockpitSessionsView: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const tc = useThemeClasses();
  const cockpitContext = useCockpitContext();

  const {
    sessions,
    sessionsLoading,
    activeSessionId,
    messages,
    messagesLoading,
    isStreaming,
    streamingText,
    streamingReasoning,
    streamingToolCalls,
    loadSessions,
    removeSession,
    renameSessionTitle,
    openSession,
    startNewSession,
    sendMessage,
    cancelStream,
  } = useCockpitStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // ── Load sessions ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (token) {
      loadSessions(token);
    }
  }, [token, loadSessions]);

  // Auto-scroll messages to bottom
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, streamingText, streamingReasoning, isStreaming, streamingToolCalls]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleOpenSession = useCallback(
    (session: CockpitSession) => {
      if (!token) return;
      openSession(token, session.id);
      setMobileShowChat(true); // Switch to chat view on mobile
    },
    [token, openSession],
  );

  const handleDelete = useCallback(
    async (sessionId: string) => {
      if (!token) return;
      setDeletingId(sessionId);
      try {
        await removeSession(token, sessionId);
      } catch (err) {
        console.error('Failed to delete session:', err);
      } finally {
        setDeletingId(null);
      }
    },
    [token, removeSession],
  );

  const handleStartRename = (session: CockpitSession) => {
    setEditingId(session.id);
    setEditTitle(session.title || '');
  };

  const handleSaveRename = async () => {
    if (!token || !editingId || !editTitle.trim()) return;
    try {
      await renameSessionTitle(token, editingId, editTitle.trim());
    } catch (err) {
      console.error('Failed to rename session:', err);
    } finally {
      setEditingId(null);
      setEditTitle('');
    }
  };

  const handleCancelRename = () => {
    setEditingId(null);
    setEditTitle('');
  };

  const handleNewConversation = useCallback(() => {
    if (!token) return;
    startNewSession(token, cockpitContext);
    setMobileShowChat(true); // Switch to chat view on mobile
  }, [token, startNewSession, cockpitContext]);

  const handleMobileBack = useCallback(() => {
    setMobileShowChat(false);
  }, []);

  const handleSend = useCallback(
    (text: string) => {
      if (!token) return;
      sendMessage(token, text, cockpitContext);
    },
    [token, cockpitContext, sendMessage],
  );

  // ── Filtered sessions ──────────────────────────────────────────────────────

  const filtered = sessions.filter((s) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (s.title || '').toLowerCase().includes(q) ||
      s.id.toLowerCase().includes(q)
    );
  });

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const activeSession = sessions.find((s) => s.id === activeSessionId);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <main className="flex-1 px-2 md:px-8 py-2 md:py-6 mt-2">
      <div
        className={cx(
          'flex rounded-2xl border shadow-sm overflow-hidden',
          tc.borderPrimary,
          tc.bgSurface,
        )}
        /* fill remaining viewport minus header + outer padding */
        style={{ height: 'calc(100vh - 8rem)' }}
      >
        {/* ── Left: Session sidebar ──────────────────────────────────────── */}
        <div className={cx(
          'flex-shrink-0 flex flex-col border-r',
          tc.borderPrimary,
          tc.bgSecondary,
          // Mobile: full width when showing sessions, hidden when showing chat
          mobileShowChat ? 'hidden md:flex' : 'flex w-full md:w-72',
        )}>
          {/* Sidebar header */}
          <div className={cx('px-4 pt-4 pb-3 space-y-3')}>
            <div className="flex items-center justify-between">
              <h2 className={cx('text-sm md:text-sm font-semibold', tc.textPrimary)}>
                {t('cockpit.sessionsTitle', 'AI Conversations')}
              </h2>
              {/* Desktop: icon only, Mobile: icon + text */}
              <button
                onClick={handleNewConversation}
                className={cx(
                  'flex items-center gap-1.5 px-2 md:px-1.5 py-1.5 rounded-lg text-white transition-colors',
                  'bg-[#025a94] hover:bg-[#037ac7]',
                )}
                title={t('cockpit.newConversation', 'New conversation')}
              >
                <PlusIcon />
                <span className="md:hidden text-xs font-medium">{t('new', 'New')}</span>
              </button>
            </div>
            {/* Search */}
            <div className="relative">
              <SearchIcon className={cx('absolute left-2.5 top-1/2 -translate-y-1/2 w-4 md:w-3.5 h-4 md:h-3.5', tc.textMuted)} />
              <input
                type="text"
                placeholder={t('cockpit.searchSessions', 'Search conversations...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cx(
                  'w-full pl-9 md:pl-8 pr-3 py-2.5 md:py-1.5 rounded-lg border text-sm md:text-xs outline-none transition-colors',
                  tc.bgPrimary,
                  tc.borderPrimary,
                  tc.textPrimary,
                  'placeholder:text-gray-400',
                  'focus:ring-1 focus:ring-blue-500/40',
                )}
              />
            </div>
          </div>

          {/* Session list */}
          <div className="flex-1 overflow-y-auto px-2 pb-2">
            {sessionsLoading && (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full" />
              </div>
            )}

            {!sessionsLoading && filtered.length === 0 && (
              <div className={cx('text-center py-10 text-xs px-2', tc.textMuted)}>
                {sessions.length === 0
                  ? t('cockpit.noSessions', 'No conversations yet')
                  : t('cockpit.noMatchingSessions', 'No matching conversations.')}
              </div>
            )}

            {filtered.map((session) => (
              <div
                key={session.id}
                className={cx(
                  'group flex items-center gap-3 md:gap-2.5 px-3 py-3.5 md:py-2.5 cursor-pointer rounded-lg mb-1 md:mb-0.5 transition-colors',
                  session.id === activeSessionId
                    ? cx('ring-1 ring-blue-500/30', tc.bgPrimary)
                    : tc.hover,
                  deletingId === session.id ? 'opacity-50 pointer-events-none' : '',
                )}
                onClick={() => editingId !== session.id && handleOpenSession(session)}
              >
                {/* Icon */}
                <div className="flex-shrink-0 w-10 h-10 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-[#025a94] to-[#84D1F1] flex items-center justify-center text-white text-[10px] md:text-[9px] font-bold shadow-sm">
                  AI
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {editingId === session.id ? (
                    <form
                      onSubmit={(e) => { e.preventDefault(); handleSaveRename(); }}
                      className="flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        autoFocus
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className={cx(
                          'flex-1 text-sm md:text-xs rounded-md border px-2 py-1.5 md:py-1 outline-none',
                          tc.bgPrimary,
                          tc.borderPrimary,
                          tc.textPrimary,
                        )}
                        onKeyDown={(e) => { if (e.key === 'Escape') handleCancelRename(); }}
                      />
                      <button type="submit" className="text-xs md:text-[10px] text-blue-500 font-medium">Save</button>
                    </form>
                  ) : (
                    <>
                      <div className={cx('text-sm md:text-xs font-medium truncate', tc.textPrimary)}>
                        {session.title || 'New conversation'}
                      </div>
                      <div className={cx('text-xs md:text-[11px] mt-0.5', tc.textMuted)}>
                        {session.message_count != null && `${session.message_count} msgs · `}
                        {formatDate(session.updated_at)}
                      </div>
                    </>
                  )}
                </div>

                {/* Actions - always visible on mobile, hover on desktop */}
                {editingId !== session.id && (
                  <div
                    className="flex items-center gap-1 md:gap-0.5 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => handleStartRename(session)}
                      className={cx('p-2 md:p-1 rounded transition-colors', tc.hover, tc.textMuted)}
                      title={t('cockpit.rename', 'Rename')}
                    >
                      <EditIcon />
                    </button>
                    <button
                      onClick={() => handleDelete(session.id)}
                      className={cx('p-2 md:p-1 rounded transition-colors', tc.hover, tc.textMuted, 'hover:text-red-500')}
                      title={t('cockpit.delete', 'Delete')}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Right: Chat area ───────────────────────────────────────────── */}
        <div className={cx(
          'flex-1 flex flex-col min-w-0', 
          tc.bgPrimary,
          // Mobile: full width when showing chat, hidden when showing sessions
          mobileShowChat ? 'flex' : 'hidden md:flex',
        )}>
          {activeSessionId ? (
            <>
              {/* Chat header — subtle, not gradient */}
              <div className={cx(
                'flex items-center gap-3 px-3 md:px-5 py-2.5 border-b',
                tc.borderPrimary,
                tc.bgSurface,
              )}>
                {/* Mobile back button */}
                <button
                  onClick={handleMobileBack}
                  className={cx(
                    'md:hidden p-1.5 -ml-1 rounded-lg transition-colors',
                    tc.hover,
                    tc.textMuted,
                  )}
                  title={t('back', 'Back')}
                >
                  <BackIcon />
                </button>
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#025a94] to-[#84D1F1] flex items-center justify-center text-white text-[9px] font-bold shadow-sm">
                  AI
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={cx('text-sm font-semibold truncate', tc.textPrimary)}>
                    {activeSession?.title || t('cockpit.newConversation', 'New conversation')}
                  </h3>
                  <span className={cx('text-[11px]', tc.textMuted)}>
                    {t('cockpit.title', 'AI Assistant')}
                  </span>
                </div>
              </div>

              {/* Messages */}
              <div ref={scrollRef} className={cx('flex-1 overflow-y-auto py-3 md:py-4 px-2 md:px-0', tc.bgPrimary)}>
                {messagesLoading && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full" />
                  </div>
                )}

                {!messagesLoading && messages.length === 0 && !isStreaming && (
                  <div className="flex flex-col items-center py-12 md:py-16 text-center px-6">
                    <div className="w-14 h-14 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-[#025a94] to-[#84D1F1] flex items-center justify-center text-white text-xl md:text-lg font-bold shadow-md mb-3">
                      AI
                    </div>
                    <h3 className={cx('text-base md:text-sm font-semibold mb-1', tc.textPrimary)}>
                      {t('cockpit.title', 'AI Assistant')}
                    </h3>
                    <p className={cx('text-sm md:text-xs', tc.textMuted)}>
                      {t('cockpit.emptyDescription', 'Ask questions about your production data')}
                    </p>
                  </div>
                )}

                {messages.map((msg) => (
                  <MessageBubble key={msg.id} message={msg} />
                ))}

                {isStreaming && (
                  <StreamingMessage
                    text={streamingText}
                    reasoning={streamingReasoning}
                    currentTool={streamingToolCalls.length > 0 ? streamingToolCalls[streamingToolCalls.length - 1] : null}
                  />
                )}
              </div>

              {/* Composer */}
              <div className={cx('border-t', tc.borderPrimary)}>
                <ChatComposer
                  onSend={handleSend}
                  onCancel={cancelStream}
                  isStreaming={isStreaming}
                  disabled={!token}
                />
              </div>
            </>
          ) : (
            /* No session selected — empty state */
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6 md:px-8">
              <div className="w-20 h-20 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-[#025a94] to-[#84D1F1] flex items-center justify-center text-white text-3xl md:text-2xl font-bold shadow-md mb-5">
                AI
              </div>
              <h2 className={cx('text-xl md:text-lg font-bold mb-1.5', tc.textPrimary)}>
                {t('cockpit.sessionsTitle', 'AI Conversations')}
              </h2>
              <p className={cx('text-base md:text-sm mb-5 max-w-sm', tc.textMuted)}>
                {t('cockpit.sessionsDescription', 'View and manage your cockpit AI conversations')}
              </p>
              <button
                onClick={handleNewConversation}
                className="px-6 py-3 md:px-5 md:py-2 rounded-xl text-base md:text-sm font-medium text-white bg-[#025a94] hover:bg-[#037ac7] transition-colors shadow-sm"
              >
                {t('cockpit.startConversation', 'Start a conversation')}
              </button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

// ─── Icons ───────────────────────────────────────────────────────────────────

const SearchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
  </svg>
);

const PlusIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
    <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
  </svg>
);

const EditIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 md:w-3.5 md:h-3.5">
    <path d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z" />
  </svg>
);

const TrashIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 md:w-3.5 md:h-3.5">
    <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clipRule="evenodd" />
  </svg>
);

const BackIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
    <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
  </svg>
);

export default CockpitSessionsView;
